import React, { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { systemAPI } from '../services/api';
import toast from 'react-hot-toast';

const SystemTenants = () => {
  const [tenants, setTenants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [creating, setCreating] = useState(false);
  const [creatingPackage, setCreatingPackage] = useState(false);
  const [savingPackage, setSavingPackage] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [passwordTenant, setPasswordTenant] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [savingPassword, setSavingPassword] = useState(false);
  const [extendTenant, setExtendTenant] = useState(null);
  const [extendForm, setExtendForm] = useState({ mode: 'months', extendMonths: '3', endDate: '' });
  const [savingExtend, setSavingExtend] = useState(false);
  const [activeSection, setActiveSection] = useState('packages');
  const [settingsForm, setSettingsForm] = useState({
    packageId: '',
    startDate: '',
    durationMonths: '1',
    enableMobileManualPunch: false,
    pricingPlan: 'standard',
    pricingCurrency: 'NPR',
    pricingModel: 'custom',
    flatAmount: '0',
    perEmployeeAmount: '0',
    pricingNote: '',
  });
  const [form, setForm] = useState({
    tenantId: '',
    name: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
  });
  const [packageForm, setPackageForm] = useState({
    name: '',
    code: '',
    description: '',
    pricingCurrency: 'NPR',
    pricingModel: 'custom',
    flatAmount: '0',
    perEmployeeAmount: '0',
    pricingNote: '',
  });

  const formatMoney = (value, currency = 'NPR') => `${(currency || 'NPR').toUpperCase()} ${Number(value || 0).toLocaleString()}`;

  const pricingSummary = (item) => {
    const currency = item?.pricingCurrency || 'NPR';
    const model = item?.pricingModel || 'custom';
    const flatAmount = Number(item?.flatAmount ?? item?.baseMonthlyPrice ?? 0);
    const perEmployeeAmount = Number(item?.perEmployeeAmount ?? item?.perEmployeePrice ?? 0);
    const note = item?.pricingNote || '';

    if (model === 'flat') return `${formatMoney(flatAmount, currency)} flat`;
    if (model === 'per-employee') return `${formatMoney(perEmployeeAmount, currency)} per employee`;
    if (model === 'mixed') return `${formatMoney(flatAmount, currency)} + ${formatMoney(perEmployeeAmount, currency)} / employee`;
    return note || 'Custom pricing';
  };

  const subscriptionStatus = (tenant) => {
    if (!tenant.packageStartDate || !tenant.packageEndDate) return null;
    const now = new Date();
    const startDate = new Date(tenant.packageStartDate);
    const endDate = new Date(tenant.packageEndDate);
    if (now < startDate) return { label: 'Scheduled', className: 'bg-gray-100 text-gray-900' };
    if (now > endDate) return { label: 'Expired', className: 'bg-red-100 text-red-700' };
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  };

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const res = await systemAPI.getTenants();
      setTenants(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPackages = useCallback(async () => {
    setLoadingPackages(true);
    try {
      const res = await systemAPI.getPackages();
      setPackages(Array.isArray(res.data) ? res.data : []);
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([loadTenants(), loadPackages()]);
  }, [loadTenants, loadPackages]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const onCreateTenant = async (e) => {
    e.preventDefault();
    if (!form.tenantId.trim() || !form.name.trim()) {
      toast.error('Tenant ID and name are required');
      return;
    }

    if (form.adminEmail && form.adminPassword && form.adminPassword.length < 6) {
      toast.error('Admin password must be at least 6 characters');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        tenantId: form.tenantId.trim().toLowerCase(),
        name: form.name.trim(),
      };
      if (form.adminEmail.trim()) {
        payload.adminEmail = form.adminEmail.trim().toLowerCase();
        if (form.adminPassword) payload.adminPassword = form.adminPassword;
        if (form.adminName.trim()) payload.adminName = form.adminName.trim();
      }
      await systemAPI.createTenant(payload);
      toast.success(form.adminEmail.trim() ? 'Tenant created with admin account' : 'Tenant created');
      setForm({ tenantId: '', name: '', adminEmail: '', adminPassword: '', adminName: '' });
      await loadTenants();
    } finally {
      setCreating(false);
    }
  };

  const toggleTenantStatus = async (tenant) => {
    await systemAPI.updateTenantStatus(tenant._id, !tenant.isActive);
    toast.success(`Tenant ${tenant.isActive ? 'deactivated' : 'activated'}`);
    await loadTenants();
  };

  const todayInputValue = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${today.getFullYear()}-${month}-${day}`;
  };

  const openPasswordReset = (tenant) => {
    setPasswordTenant(tenant);
    setPasswordForm({ newPassword: '', confirmPassword: '' });
  };

  const openExtendSubscription = (tenant) => {
    setExtendTenant(tenant);
    setExtendForm({ mode: 'months', extendMonths: '3', endDate: todayInputValue() });
  };

  const savePasswordReset = async () => {
    if (!passwordTenant?._id) return;
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      const res = await systemAPI.resetTenantAdminPassword(passwordTenant._id, passwordForm.newPassword);
      toast.success(`Password reset for ${res.data?.adminEmail || 'tenant admin'}`);
      setPasswordTenant(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to reset password');
    } finally {
      setSavingPassword(false);
    }
  };

  const saveExtendSubscription = async () => {
    if (!extendTenant?._id) return;

    const payload = extendForm.mode === 'date'
      ? { endDate: extendForm.endDate }
      : { extendMonths: Number(extendForm.extendMonths) };

    if (extendForm.mode === 'date' && !extendForm.endDate) {
      toast.error('Choose a new end date');
      return;
    }

    setSavingExtend(true);
    try {
      await systemAPI.extendTenantSubscription(extendTenant._id, payload);
      toast.success('Subscription extended');
      setExtendTenant(null);
      await loadTenants();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to extend subscription');
    } finally {
      setSavingExtend(false);
    }
  };

  const onCreatePackage = async (e) => {
    e.preventDefault();
    if (!packageForm.name.trim()) {
      toast.error('Package name is required');
      return;
    }

    const flatAmount = Number(packageForm.flatAmount);
    const perEmployeeAmount = Number(packageForm.perEmployeeAmount);
    if (Number.isNaN(flatAmount) || Number.isNaN(perEmployeeAmount)) {
      toast.error('Package pricing must be valid numbers');
      return;
    }
    if (flatAmount < 0 || perEmployeeAmount < 0) {
      toast.error('Package pricing cannot be negative');
      return;
    }

    setCreatingPackage(true);
    try {
      await systemAPI.createPackage({
        name: packageForm.name.trim(),
        code: packageForm.code.trim() || undefined,
        description: packageForm.description.trim() || undefined,
        pricingCurrency: packageForm.pricingCurrency.trim().toUpperCase() || 'NPR',
        pricingModel: packageForm.pricingModel,
        flatAmount,
        perEmployeeAmount,
        pricingNote: packageForm.pricingNote.trim(),
      });
      toast.success('Package created');
      setPackageForm({
        name: '',
        code: '',
        description: '',
        pricingCurrency: 'NPR',
        pricingModel: 'custom',
        flatAmount: '0',
        perEmployeeAmount: '0',
        pricingNote: '',
      });
      await loadPackages();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to create package');
    } finally {
      setCreatingPackage(false);
    }
  };

  const openPackageEditor = (pkg) => {
    setEditingPackage(pkg);
    setPackageForm({
      name: pkg.name || '',
      code: pkg.code || '',
      description: pkg.description || '',
      pricingCurrency: pkg.pricingCurrency || 'NPR',
      pricingModel: pkg.pricingModel || 'custom',
      flatAmount: String(pkg.flatAmount ?? pkg.baseMonthlyPrice ?? 0),
      perEmployeeAmount: String(pkg.perEmployeeAmount ?? pkg.perEmployeePrice ?? 0),
      pricingNote: pkg.pricingNote || '',
    });
  };

  const savePackage = async () => {
    if (!editingPackage?._id) return;

    const flatAmount = Number(packageForm.flatAmount);
    const perEmployeeAmount = Number(packageForm.perEmployeeAmount);
    if (Number.isNaN(flatAmount) || Number.isNaN(perEmployeeAmount)) {
      toast.error('Package pricing must be valid numbers');
      return;
    }
    if (flatAmount < 0 || perEmployeeAmount < 0) {
      toast.error('Package pricing cannot be negative');
      return;
    }

    setSavingPackage(true);
    try {
      await systemAPI.updatePackage(editingPackage._id, {
        name: packageForm.name.trim(),
        code: packageForm.code.trim() || undefined,
        description: packageForm.description.trim() || undefined,
        pricingCurrency: packageForm.pricingCurrency.trim().toUpperCase() || 'NPR',
        pricingModel: packageForm.pricingModel,
        flatAmount,
        perEmployeeAmount,
        pricingNote: packageForm.pricingNote.trim(),
        isActive: editingPackage.isActive,
      });
      toast.success('Package updated');
      setEditingPackage(null);
      await loadPackages();
      await loadTenants();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update package');
    } finally {
      setSavingPackage(false);
    }
  };

  const openSettingsEditor = (tenant) => {
    const packageStartDate = tenant.packageStartDate ? new Date(tenant.packageStartDate) : null;
    setEditingTenant(tenant);
    setSettingsForm({
      packageId: tenant.linkedPackageId || '',
      startDate: packageStartDate ? packageStartDate.toISOString().slice(0, 10) : '',
      durationMonths: String(tenant.packageDurationMonths || 1),
      enableMobileManualPunch: !!tenant.enableMobileManualPunch,
      pricingPlan: tenant.pricingPlan || 'standard',
      pricingCurrency: tenant.pricingCurrency || 'NPR',
      pricingModel: tenant.pricingModel || 'custom',
      flatAmount: String(tenant.flatAmount ?? tenant.baseMonthlyPrice ?? 0),
      perEmployeeAmount: String(tenant.perEmployeeAmount ?? tenant.perEmployeePrice ?? 0),
      pricingNote: tenant.pricingNote || '',
    });
  };

  const saveTenantSettings = async () => {
    if (!editingTenant?._id) return;
    const flatAmount = Number(settingsForm.flatAmount);
    const perEmployeeAmount = Number(settingsForm.perEmployeeAmount);
    if (Number.isNaN(flatAmount) || Number.isNaN(perEmployeeAmount)) {
      toast.error('Pricing values must be valid numbers');
      return;
    }
    if (flatAmount < 0 || perEmployeeAmount < 0) {
      toast.error('Pricing values cannot be negative');
      return;
    }

    setSavingSettings(true);
    try {
      await systemAPI.updateTenantPackage(editingTenant._id, settingsForm.packageId || null, {
        startDate: settingsForm.startDate || null,
        durationMonths: Number(settingsForm.durationMonths) || null,
      });

      try {
        await systemAPI.updateTenantSettings(editingTenant._id, {
          enableMobileManualPunch: settingsForm.enableMobileManualPunch,
          pricingPlan: settingsForm.pricingPlan.trim() || 'standard',
          pricingCurrency: settingsForm.pricingCurrency.trim().toUpperCase() || 'NPR',
          pricingModel: settingsForm.pricingModel,
          flatAmount,
          perEmployeeAmount,
          pricingNote: settingsForm.pricingNote.trim(),
        });
      } catch (error) {
        // Backward compatibility for deployments that don't yet expose /settings
        if (error?.response?.status === 404) {
          await systemAPI.updateTenantMobileManualPunch(editingTenant._id, settingsForm.enableMobileManualPunch);
        } else {
          throw error;
        }
      }
      toast.success(`Settings updated for ${editingTenant.tenantId}`);
      setEditingTenant(null);
      await loadTenants();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update tenant settings');
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Tenant Management</h1>
          <p className="text-sm text-gray-600 mt-1">Use the menu below to switch between package and tenant management.</p>
        </div>
        <button
          onClick={refreshAll}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 rounded-md border border-gray-200 bg-white p-2 shadow-sm w-fit">
        <button
          type="button"
          onClick={() => setActiveSection('packages')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeSection === 'packages' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          Packages
        </button>
        <button
          type="button"
          onClick={() => setActiveSection('tenants')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${activeSection === 'tenants' ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
        >
          Tenants
        </button>
      </div>

      {activeSection === 'packages' && (
        <div className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Package</h2>
            <form onSubmit={onCreatePackage} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Package Name</span>
                  <input
                    type="text"
                    className="input"
                    placeholder="Starter"
                    value={packageForm.name}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Package Code</span>
                  <input
                    type="text"
                    className="input"
                    placeholder="starter-plan"
                    value={packageForm.code}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, code: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Currency</span>
                  <input
                    type="text"
                    className="input uppercase"
                    maxLength={5}
                    placeholder="NPR"
                    value={packageForm.pricingCurrency}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingCurrency: e.target.value }))}
                  />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-gray-600">Pricing Model</span>
                  <select
                    value={packageForm.pricingModel}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingModel: e.target.value }))}
                    className="input"
                  >
                    <option value="custom">Custom</option>
                    <option value="flat">Flat amount</option>
                    <option value="per-employee">Per employee</option>
                    <option value="mixed">Flat + per employee</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Flat Amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input"
                    placeholder="0"
                    value={packageForm.flatAmount}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, flatAmount: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Per Employee Amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input"
                    placeholder="0"
                    value={packageForm.perEmployeeAmount}
                    onChange={(e) => setPackageForm((prev) => ({ ...prev, perEmployeeAmount: e.target.value }))}
                  />
                </label>
              </div>
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600">Description</span>
                <textarea
                  className="input min-h-[72px]"
                  placeholder="Optional package description"
                  value={packageForm.description}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </label>
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600">Pricing Note</span>
                <textarea
                  className="input min-h-[72px]"
                  placeholder="Example: 50 per employee or any custom billing note"
                  value={packageForm.pricingNote}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingNote: e.target.value }))}
                />
              </label>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creatingPackage}
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {creatingPackage ? 'Creating...' : 'Create Package'}
                </button>
              </div>
            </form>

            <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 uppercase tracking-wide">Available Packages</div>
              {loadingPackages ? (
                <div className="p-4 text-sm text-gray-600">Loading packages...</div>
              ) : packages.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">No packages found.</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {packages.map((pkg) => (
                    <div key={pkg._id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{pkg.name}</p>
                        <p className="text-xs text-gray-600">{pricingSummary(pkg)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium w-fit ${pkg.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {pkg.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          type="button"
                          onClick={() => openPackageEditor(pkg)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-900 hover:bg-gray-100"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeSection === 'tenants' && (
        <div className="space-y-6">
          <div className="bg-white rounded-md border border-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Package Overview</h2>
                <p className="text-sm text-gray-600 mt-1">Visible to admin for quick review before linking packages to tenants.</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold text-gray-900">{packages.length}</p>
                <p className="text-xs text-gray-500">Total packages</p>
              </div>
            </div>

            {packages.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-600">
                No packages have been created yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {packages.map((pkg) => (
                  <div key={pkg._id} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-semibold text-gray-900">{pkg.name}</p>
                        <p className="text-xs text-gray-500 font-mono">{pkg.code}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 rounded-full text-[11px] font-semibold ${pkg.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {pkg.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mt-2">{pricingSummary(pkg)}</p>
                    {pkg.pricingNote && <p className="text-xs text-gray-500 mt-2">{pkg.pricingNote}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-md border border-gray-200 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Create Tenant</h2>
            <form onSubmit={onCreateTenant} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Tenant ID</span>
                  <input
                    type="text"
                    className="input"
                    placeholder="acme"
                    value={form.tenantId}
                    onChange={(e) => setForm((prev) => ({ ...prev, tenantId: e.target.value }))}
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Tenant Display Name</span>
                  <input
                    type="text"
                    className="input"
                    placeholder="Acme Pvt. Ltd."
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </label>
              </div>
              <p className="text-xs text-gray-500 font-medium pt-1">Admin Account <span className="font-normal text-gray-400">(optional — allows tenant admin login)</span></p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  className="input"
                  placeholder="Admin Name"
                  value={form.adminName}
                  onChange={(e) => setForm((prev) => ({ ...prev, adminName: e.target.value }))}
                />
                <input
                  type="email"
                  className="input"
                  placeholder="Admin Email"
                  value={form.adminEmail}
                  onChange={(e) => setForm((prev) => ({ ...prev, adminEmail: e.target.value }))}
                />
                <input
                  type="password"
                  className="input"
                  placeholder="Admin Password (min 6 chars)"
                  value={form.adminPassword}
                  onChange={(e) => setForm((prev) => ({ ...prev, adminPassword: e.target.value }))}
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={creating}
                  className="btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {creating ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-md border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Tenants</h2>
            </div>
            {loading ? (
              <div className="p-6 text-sm text-gray-600">Loading tenants...</div>
            ) : tenants.length === 0 ? (
              <div className="p-6 text-sm text-gray-600">No tenants found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tenant ID</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linked Package</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile Manual Punch</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pricing</th>
                      <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tenants.map((tenant) => (
                      <tr key={tenant._id}>
                        <td className="px-5 py-4 text-sm font-mono text-gray-900">{tenant.tenantId}</td>
                        <td className="px-5 py-4 text-sm text-gray-800">
                          <div>{tenant.name}</div>
                          {tenant.adminEmail && (
                            <div className="text-xs text-gray-500">{tenant.adminEmail}</div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${tenant.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {tenant.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-800">
                          <div className="space-y-1">
                            <p>{tenant.linkedPackage?.name || 'Manual'}</p>
                            {tenant.packageStartDate && tenant.packageEndDate && (
                              <p className="text-xs text-gray-500">
                                {new Date(tenant.packageStartDate).toLocaleDateString()} - {new Date(tenant.packageEndDate).toLocaleDateString()}
                              </p>
                            )}
                            {subscriptionStatus(tenant) && (
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${subscriptionStatus(tenant).className}`}>
                                {subscriptionStatus(tenant).label}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span
                            className={`inline-flex px-3 py-1.5 rounded-lg text-xs font-semibold ${tenant.enableMobileManualPunch ? 'bg-gray-100 text-gray-900' : 'bg-gray-100 text-gray-700'}`}
                          >
                            {tenant.enableMobileManualPunch ? 'Enabled' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-800">
                          <div className="space-y-1">
                            <p className="font-medium capitalize">{tenant.pricingPlan || 'standard'}</p>
                            <p className="text-xs text-gray-600">{pricingSummary(tenant)}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-col items-end gap-1.5">
                            <button
                              onClick={() => openSettingsEditor(tenant)}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-900 hover:bg-gray-200"
                            >
                              Edit Settings
                            </button>
                            <button
                              onClick={() => openPasswordReset(tenant)}
                              disabled={!tenant.adminEmail}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                            >
                              Reset password
                            </button>
                            <button
                              onClick={() => openExtendSubscription(tenant)}
                              disabled={!tenant.linkedPackageId}
                              className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-40"
                            >
                              Extend dates
                            </button>
                            <button
                              onClick={() => toggleTenantStatus(tenant)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${tenant.isActive ? 'bg-red-50 text-red-700 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                            >
                              {tenant.isActive ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {editingTenant && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md border border-gray-200 shadow-lg w-full max-w-md p-5 space-y-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Edit Tenant Settings</h3>
              <p className="text-sm text-gray-600 mt-1">
                Tenant: <span className="font-mono">{editingTenant.tenantId}</span>
              </p>
            </div>

            <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Mobile Manual Punch</p>
                <p className="text-xs text-gray-500">Allow employee punch in/out manually from mobile app.</p>
              </div>
              <input
                type="checkbox"
                checked={settingsForm.enableMobileManualPunch}
                onChange={(e) => setSettingsForm((prev) => ({ ...prev, enableMobileManualPunch: e.target.checked }))}
                className="h-4 w-4"
              />
            </label>

            <label className="space-y-1 block">
              <span className="text-xs font-medium text-gray-600">Linked Package</span>
              <select
                value={settingsForm.packageId}
                onChange={(e) => {
                  const nextPackageId = e.target.value;
                  const selectedPackage = packages.find((pkg) => pkg._id === nextPackageId);
                  setSettingsForm((prev) => ({
                    ...prev,
                    packageId: nextPackageId,
                    pricingPlan: selectedPackage?.name || prev.pricingPlan,
                    pricingCurrency: selectedPackage?.pricingCurrency || prev.pricingCurrency,
                    pricingModel: selectedPackage?.pricingModel || prev.pricingModel,
                    flatAmount:
                      selectedPackage ? String(selectedPackage.flatAmount ?? selectedPackage.baseMonthlyPrice ?? 0) : prev.flatAmount,
                    perEmployeeAmount:
                      selectedPackage ? String(selectedPackage.perEmployeeAmount ?? selectedPackage.perEmployeePrice ?? 0) : prev.perEmployeeAmount,
                    pricingNote: selectedPackage?.pricingNote || prev.pricingNote,
                  }));
                }}
                className="input"
              >
                <option value="">Manual / No Package</option>
                {packages
                  .filter((pkg) => pkg.isActive)
                  .map((pkg) => (
                    <option key={pkg._id} value={pkg._id}>
                      {pkg.name} ({pricingSummary(pkg)})
                    </option>
                  ))}
              </select>
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Package Start Date</span>
                <input
                  type="date"
                  value={settingsForm.startDate}
                  onChange={(e) => setSettingsForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  className="input"
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Duration</span>
                <select
                  value={settingsForm.durationMonths}
                  onChange={(e) => setSettingsForm((prev) => ({ ...prev, durationMonths: e.target.value }))}
                  className="input"
                >
                  <option value="1">1 month</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="24">24 months</option>
                </select>
              </label>
            </div>

            <div className="rounded-lg border border-gray-200 p-3 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Pricing Settings</p>
                <p className="text-xs text-gray-500">Define billing model for this tenant.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Plan</span>
                  <input
                    type="text"
                    value={settingsForm.pricingPlan}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, pricingPlan: e.target.value }))}
                    className="input"
                    placeholder="standard"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Pricing Model</span>
                  <select
                    value={settingsForm.pricingModel}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, pricingModel: e.target.value }))}
                    className="input"
                  >
                    <option value="custom">Custom</option>
                    <option value="flat">Flat amount</option>
                    <option value="per-employee">Per employee</option>
                    <option value="mixed">Flat + per employee</option>
                  </select>
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Currency</span>
                  <input
                    type="text"
                    maxLength={5}
                    value={settingsForm.pricingCurrency}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, pricingCurrency: e.target.value }))}
                    className="input uppercase"
                    placeholder="NPR"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Flat Amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settingsForm.flatAmount}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, flatAmount: e.target.value }))}
                    className="input"
                    placeholder="0"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs font-medium text-gray-600">Per Employee Amount</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={settingsForm.perEmployeeAmount}
                    onChange={(e) => setSettingsForm((prev) => ({ ...prev, perEmployeeAmount: e.target.value }))}
                    className="input"
                    placeholder="0"
                  />
                </label>
              </div>
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600">Pricing Note</span>
                <textarea
                  value={settingsForm.pricingNote}
                  onChange={(e) => setSettingsForm((prev) => ({ ...prev, pricingNote: e.target.value }))}
                  className="input min-h-[72px]"
                  placeholder="Any special billing terms for this tenant"
                />
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEditingTenant(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={saveTenantSettings}
                disabled={savingSettings}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {savingSettings ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingPackage && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md border border-gray-200 shadow-lg w-full max-w-lg p-5 space-y-4 max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Edit Package</h3>
              <p className="text-sm text-gray-600 mt-1">
                Package: <span className="font-mono">{editingPackage.name}</span>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Package Name</span>
                <input
                  type="text"
                  className="input"
                  value={packageForm.name}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Package Code</span>
                <input
                  type="text"
                  className="input"
                  value={packageForm.code}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, code: e.target.value }))}
                />
              </label>
            </div>

            <label className="space-y-1 block">
              <span className="text-xs font-medium text-gray-600">Description</span>
              <textarea
                className="input min-h-[72px]"
                value={packageForm.description}
                onChange={(e) => setPackageForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Pricing Model</span>
                <select
                  value={packageForm.pricingModel}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingModel: e.target.value }))}
                  className="input"
                >
                  <option value="custom">Custom</option>
                  <option value="flat">Flat amount</option>
                  <option value="per-employee">Per employee</option>
                  <option value="mixed">Flat + per employee</option>
                </select>
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Currency</span>
                <input
                  type="text"
                  className="input uppercase"
                  maxLength={5}
                  value={packageForm.pricingCurrency}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingCurrency: e.target.value }))}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Flat Amount</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  value={packageForm.flatAmount}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, flatAmount: e.target.value }))}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-medium text-gray-600">Per Employee Amount</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input"
                  value={packageForm.perEmployeeAmount}
                  onChange={(e) => setPackageForm((prev) => ({ ...prev, perEmployeeAmount: e.target.value }))}
                />
              </label>
            </div>

            <label className="space-y-1 block">
              <span className="text-xs font-medium text-gray-600">Pricing Note</span>
              <textarea
                className="input min-h-[72px]"
                value={packageForm.pricingNote}
                onChange={(e) => setPackageForm((prev) => ({ ...prev, pricingNote: e.target.value }))}
              />
            </label>

            <label className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 p-3">
              <div>
                <p className="text-sm font-medium text-gray-900">Active Package</p>
                <p className="text-xs text-gray-500">Inactive packages cannot be assigned to new tenants.</p>
              </div>
              <input
                type="checkbox"
                checked={!!editingPackage.isActive}
                onChange={(e) => setEditingPackage((prev) => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4"
              />
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <button
                onClick={() => setEditingPackage(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={savePackage}
                disabled={savingPackage}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {savingPackage ? 'Saving...' : 'Save Package'}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordTenant && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md border border-gray-200 shadow-lg w-full max-w-md p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Reset admin password</h3>
              <p className="text-sm text-gray-600 mt-1">
                {passwordTenant.adminName || 'Tenant admin'} · {passwordTenant.adminEmail}
              </p>
              <p className="text-xs text-gray-500 mt-1 font-mono">{passwordTenant.tenantId}</p>
            </div>
            <label className="space-y-1 block">
              <span className="text-xs font-medium text-gray-600">New password</span>
              <input
                type="password"
                className="input"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))}
              />
            </label>
            <label className="space-y-1 block">
              <span className="text-xs font-medium text-gray-600">Confirm password</span>
              <input
                type="password"
                className="input"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </label>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setPasswordTenant(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={savePasswordReset}
                disabled={savingPassword}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {savingPassword ? 'Saving...' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {extendTenant && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-md border border-gray-200 shadow-lg w-full max-w-md p-5 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Extend subscription</h3>
              <p className="text-sm text-gray-600 mt-1">
                {extendTenant.name} · <span className="font-mono">{extendTenant.tenantId}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Current end:{' '}
                {extendTenant.packageEndDate
                  ? new Date(extendTenant.packageEndDate).toLocaleDateString()
                  : 'Not set'}
                {subscriptionStatus(extendTenant) ? ` · ${subscriptionStatus(extendTenant).label}` : ''}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setExtendForm((prev) => ({ ...prev, mode: 'months' }))}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${extendForm.mode === 'months' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Add months
              </button>
              <button
                type="button"
                onClick={() => setExtendForm((prev) => ({ ...prev, mode: 'date' }))}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${extendForm.mode === 'date' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}
              >
                Set end date
              </button>
            </div>

            {extendForm.mode === 'months' ? (
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600">Extend by</span>
                <select
                  value={extendForm.extendMonths}
                  onChange={(e) => setExtendForm((prev) => ({ ...prev, extendMonths: e.target.value }))}
                  className="input"
                >
                  <option value="1">1 month</option>
                  <option value="3">3 months</option>
                  <option value="6">6 months</option>
                  <option value="12">12 months</option>
                  <option value="24">24 months</option>
                </select>
                <p className="text-xs text-gray-500">
                  If the current end date is already past, the new period starts from today. An expired tenant is activated again.
                </p>
              </label>
            ) : (
              <label className="space-y-1 block">
                <span className="text-xs font-medium text-gray-600">New end date</span>
                <input
                  type="date"
                  min={todayInputValue()}
                  value={extendForm.endDate}
                  onChange={(e) => setExtendForm((prev) => ({ ...prev, endDate: e.target.value }))}
                  className="input"
                />
              </label>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setExtendTenant(null)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveExtendSubscription}
                disabled={savingExtend}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {savingExtend ? 'Saving...' : 'Extend'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemTenants;
